import { TimelinesGridPrecisionUnit, UnitOfTime } from '@ardoq/api-types';
import {
  add,
  endOfISOWeek,
  getISOWeek,
  getQuarter,
  getYear,
  isCurrentLocaleTimeFormat12Hour,
  startOfISOWeek,
} from '@ardoq/date-time';
import { getCurrentLocale } from '@ardoq/locale';
import {
  composeLabelIntlFormatOptionsForGrid,
  getPreviousTick,
  getTickLabelLeft,
} from './calculations';
import { TimeAxisLabelInfo, TimeAxisRowSegmentLabelInfo } from '../types';
import {
  measureGridLabelTextWidth,
  formatDateBasedOnPrecision,
} from '../utils';
import { ChartDimensions, TimeUnscalingFunction } from '@ardoq/graph';
import { CROSSHAIR_HEIGHT, CROSSHAIR_LABEL_PADDING_X } from './consts';

// getWeekLabelForDailyGrid(tick)

const formatDateForTickLabel = (
  tick: Date,
  selectedGrid: TimelinesGridPrecisionUnit
) => {
  switch (selectedGrid) {
    // Intl format
    case UnitOfTime.DAY:
    case UnitOfTime.MONTH:
    case UnitOfTime.YEAR:
      return formatIntlDate(
        tick,
        composeLabelIntlFormatOptionsForGrid(selectedGrid)
      );
    // custom format
    case UnitOfTime.WEEK:
      return String(getISOWeek(tick));
    case UnitOfTime.QUARTER:
      return String(`Q${getQuarter(tick)}`);
    default:
      return '';
  }
};

export const formatIntlDate = (
  date: Date,
  formatOptions: Intl.DateTimeFormatOptions
) => {
  const locale = getCurrentLocale();

  return new Intl.DateTimeFormat(locale.code, formatOptions).format(date);
};

/**
 * This function does not handle the special case of week labels in daily grid,
 * call getTickLabel instead
 */
const _getStandardRowTickLabel = (
  tick: Date,
  selectedGrid: TimelinesGridPrecisionUnit,
  numberOfUnitsForMultiUnitTick = 0
) => {
  const tickValueLabel = formatDateForTickLabel(tick, selectedGrid);
  if (numberOfUnitsForMultiUnitTick > 1) {
    return `${tickValueLabel} - ${formatDateForTickLabel(
      add(tick, { [selectedGrid]: numberOfUnitsForMultiUnitTick - 1 }),
      selectedGrid
    )}`;
  }
  return tickValueLabel;
};

type GetTickLabelArgs = {
  tick: Date;
  gridPrecision: TimelinesGridPrecisionUnit;
  numberOfUnitsToPreviousTick?: number;
  isWeekLabelInDailyGrid?: boolean;
};
export const getTickLabel = ({
  tick,
  gridPrecision,
  numberOfUnitsToPreviousTick,
  isWeekLabelInDailyGrid,
}: GetTickLabelArgs) => {
  return isWeekLabelInDailyGrid
    ? // isWeekLabelInDailyGrid is a special case for daily grid where the top row
      // label should be a week label instead of the standard row label
      getWeekLabelForDailyGrid(tick)
    : _getStandardRowTickLabel(
        tick,
        gridPrecision,
        numberOfUnitsToPreviousTick
      );
};

const getPreviousTickLabel = ({
  tick,
  gridPrecision,
  numberOfUnitsToPreviousTick,
  isWeekLabelInDailyGrid,
}: GetTickLabelArgs) => {
  const previousTick = getPreviousTick(
    tick,
    gridPrecision,
    numberOfUnitsToPreviousTick
  );

  return getTickLabel({
    tick: previousTick,
    gridPrecision,
    numberOfUnitsToPreviousTick,
    isWeekLabelInDailyGrid,
  });
};

/**
 * Daily grid requires a specialized composition of the top row label
 */
const getWeekLabelForDailyGrid = (date: Date) => {
  const firstWeekDay = startOfISOWeek(date);
  const firstDayYear = getYear(firstWeekDay);
  const lasWeektDay = endOfISOWeek(date);
  const lastDayYear = getYear(lasWeektDay);

  return `${formatDayLabelPartForWeekLabelInDayGrid(firstWeekDay)}${
    firstDayYear === lastDayYear ? '' : `, ${firstDayYear}`
  } - ${formatDayLabelPartForWeekLabelInDayGrid(
    lasWeektDay
  )}, ${lastDayYear} (Week ${getISOWeek(date)})`;
};
const formatDayLabelPartForWeekLabelInDayGrid = (date: Date) =>
  formatIntlDate(date, {
    month: 'short',
    day: 'numeric',
  });

type GetTimeAxisRowSegmentLabelInfoArgs = {
  tickLabelInfo: TimeAxisLabelInfo;
  isWeekLabelInDailyGrid: boolean;
  gridPrecision: TimelinesGridPrecisionUnit;
  numberOfUnitsToPreviousTick: number;
  isFirstLabel: boolean;
  isLastLabel: boolean;
  tickDistance: number;
  dimensions: ChartDimensions;
};
export const getTimeAxisRowSegmentLabelInfo = ({
  tickLabelInfo,
  isWeekLabelInDailyGrid,
  gridPrecision,
  numberOfUnitsToPreviousTick,
  isFirstLabel,
  isLastLabel,
  tickDistance,
  dimensions: {
    margin,
    chart: { width },
  },
}: GetTimeAxisRowSegmentLabelInfoArgs): TimeAxisRowSegmentLabelInfo => {
  const { tickPosition, tickSize, tickValue, tickLabel } = tickLabelInfo;

  const previousTickLabelText = isFirstLabel
    ? getPreviousTickLabel({
        tick: tickValue,
        gridPrecision,
        numberOfUnitsToPreviousTick,
        isWeekLabelInDailyGrid,
      })
    : null;

  const showPreviousTickLabel =
    previousTickLabelText &&
    tickPosition - margin.left >
      (isWeekLabelInDailyGrid
        ? measureGridLabelTextWidth(previousTickLabelText)
        : tickSize);

  // show the labels only if they fit between the grid line and the chart edge
  const showCurrentTickLabel =
    !isLastLabel || margin.left + width - tickPosition > tickSize;

  return {
    previousTickLabel: showPreviousTickLabel
      ? {
          labelText: previousTickLabelText,
          labelLeftPosition: (tickPosition + margin.left) * 0.5,
        }
      : null,
    tickPosition,
    currentTickLabel: showCurrentTickLabel
      ? {
          labelText: tickLabel,
          labelLeftPosition: getTickLabelLeft(tickLabelInfo, tickDistance),
        }
      : null,
  };
};

type GetNoXAxisTicksLabelArgs = {
  visibleDomain: [Date, Date];
  gridPrecision: TimelinesGridPrecisionUnit;
  isWeekLabelInDailyGrid: boolean;
};
export const getNoXAxisTicksLabel = ({
  visibleDomain,
  gridPrecision,
  isWeekLabelInDailyGrid,
}: GetNoXAxisTicksLabelArgs) => {
  const domainStartLabel = getTickLabel({
    tick: visibleDomain[0],
    gridPrecision,
    isWeekLabelInDailyGrid,
  });
  const domainEndLabel = getTickLabel({
    tick: visibleDomain[1],
    gridPrecision,
    isWeekLabelInDailyGrid,
  });
  return domainStartLabel === domainEndLabel
    ? domainStartLabel
    : `${domainStartLabel} - ${domainEndLabel}`;
};

type CalculateCrosshairXPropsArgs = {
  crosshairXPosition: number;
  chartAreaWidth: number;
  xAxisContainerHeight: number;
  unscaler: TimeUnscalingFunction;
  gridPrecision: TimelinesGridPrecisionUnit;
};
export const calculateCrosshairXProps = ({
  crosshairXPosition,
  chartAreaWidth,
  xAxisContainerHeight,
  unscaler,
  gridPrecision,
}: CalculateCrosshairXPropsArgs) => {
  const crosshairY = xAxisContainerHeight - CROSSHAIR_HEIGHT;
  const locale = getCurrentLocale();
  const labelText = formatDateBasedOnPrecision({
    date: unscaler(crosshairXPosition),
    precision: gridPrecision,
    isLocaleTimeFormat12Hour: isCurrentLocaleTimeFormat12Hour(locale),
    currentLocale: locale,
  });
  const labelWidth =
    measureGridLabelTextWidth(labelText) + CROSSHAIR_LABEL_PADDING_X * 2;
  const isLabelPositionLeft =
    labelWidth && crosshairXPosition + labelWidth > chartAreaWidth;

  const left = isLabelPositionLeft
    ? crosshairXPosition - labelWidth
    : crosshairXPosition;

  return {
    labelText,
    crosshairXPosition,
    left,
    top: crosshairY,
  };
};
